/**
 * 📜 BLUEPRINT LOG SERVICE
 *
 * Serviço para registrar e consultar o log de governança
 * de process_blueprints.
 *
 * Ações rastreadas:
 *   created · published · versioned · set_standard
 *   removed_standard · archived · reactivated · edited · cloned
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../../../lib/supabaseClient';
import type {
  ProcessBlueprintLog,
  BlueprintLogAction,
} from '../../../types/processBlueprint';

// ============================================================================
// HELPERS
// ============================================================================

function mapRowToLog(row: Record<string, any>): ProcessBlueprintLog {
  return {
    id: row.id,
    blueprintId: row.blueprint_id,
    organizationId: row.organization_id,
    action: row.action,
    performedBy: row.performed_by ?? undefined,
    details: row.details ?? {},
    createdAt: row.created_at,
  };
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Registra uma entrada no log de governança.
 * Faz fire-and-forget — não deve bloquear o fluxo principal.
 */
export async function logGovernanceAction(params: {
  blueprintId: string;
  organizationId: string;
  action: BlueprintLogAction;
  performedBy?: string;
  details?: Record<string, unknown>;
}): Promise<void> {
  const { blueprintId, organizationId, action, performedBy, details } = params;

  const insertPayload: Record<string, any> = {
    blueprint_id: blueprintId,
    organization_id: organizationId,
    action,
    details: details ?? {},
  };

  const { error } = await supabase
    .from('process_blueprint_logs')
    .insert(insertPayload);

  if (error) {
    // Log silencioso — não deve quebrar o fluxo de governança
    console.error('[BlueprintLog] Falha ao registrar log:', error.message);
  }
}

// ============================================================================
// READ
// ============================================================================

/**
 * Lista o histórico de governança de um blueprint específico.
 * Ordenado por created_at DESC (mais recente primeiro).
 */
export async function listBlueprintLogs(
  blueprintId: string,
): Promise<ProcessBlueprintLog[]> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_logs')
    .select('*')
    .eq('blueprint_id', blueprintId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapRowToLog);
}

/**
 * Lista o histórico de governança de toda a organização.
 * Útil para auditoria geral. Limitado a 200 registros.
 */
export async function listOrganizationLogs(
  organizationId: string,
  limit = 200,
): Promise<ProcessBlueprintLog[]> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_logs')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(mapRowToLog);
}
